// A compact, Dust2-flavoured arena: two opposing spawns (T south, CT
// north), an open mid, two bomb-sites (A east, B west) framed by crates
// for cover, and connecting corridors. It's not a 1:1 Dust2 replica —
// it's a readable, symmetric layout that plays well for a first build.
//
// Coordinates: X = east/west, Z = north/south, Y = up. Ground top = 0.
// Boxes are stored as center + half-extents, which suits both AABB
// collision and THREE.BoxGeometry.

import { Team } from "./constants";

export type BoxKind = "wall" | "crate";

export type MapBox = {
  cx: number;
  cy: number;
  cz: number;
  hw: number; // half width  (X)
  hh: number; // half height (Y)
  hd: number; // half depth  (Z)
  color: number;
  collide: boolean;
  kind: BoxKind;
};

export type SpawnPoint = { x: number; z: number; yaw: number };

export type FpsMap = {
  ground: { hw: number; hd: number; color: number };
  boxes: MapBox[];
  spawns: Record<Team, SpawnPoint[]>;
  sites: Array<{ name: string; x: number; z: number }>;
  bounds: { minX: number; maxX: number; minZ: number; maxZ: number };
};

// Palette — sandy Dust2 tones.
const SAND = 0xc2b280;
const WALL = 0xb0a070;
const WALL_DARK = 0x8f7f55;
const CRATE = 0xa0703c;
const CRATE_LIGHT = 0xc08a4e;
const HALF_X = 40;
const HALF_Z = 30;

/** center-based box helper; `y` is the base (floor) height. */
function box(
  x: number,
  z: number,
  w: number,
  d: number,
  h: number,
  color: number,
  y = 0,
  collide = true,
): MapBox {
  return {
    cx: x,
    cz: z,
    cy: y + h / 2,
    hw: w / 2,
    hd: d / 2,
    hh: h / 2,
    color,
    collide,
    kind: color === CRATE || color === CRATE_LIGHT ? "crate" : "wall",
  };
}

function buildMap(): FpsMap {
  const boxes: MapBox[] = [];
  const WALL_H = 6;
  const T = 1; // wall thickness

  // ─── Perimeter walls ───
  boxes.push(box(0, HALF_Z, HALF_X * 2, T, WALL_H, WALL)); // north (CT)
  boxes.push(box(0, -HALF_Z, HALF_X * 2, T, WALL_H, WALL)); // south (T)
  boxes.push(box(HALF_X, 0, T, HALF_Z * 2, WALL_H, WALL)); // east
  boxes.push(box(-HALF_X, 0, T, HALF_Z * 2, WALL_H, WALL)); // west

  // ─── Mid divider walls (create A-side / B-side lanes with gaps) ───
  // Two vertical walls flanking a central mid lane, each with a doorway
  // gap toward the middle of the map.
  boxes.push(box(13, 12, T, 22, WALL_H, WALL_DARK)); // A/mid wall (north half)
  boxes.push(box(13, -14, T, 18, WALL_H, WALL_DARK)); // A/mid wall (south half)
  boxes.push(box(-13, 12, T, 22, WALL_H, WALL_DARK)); // B/mid wall (north half)
  boxes.push(box(-13, -14, T, 18, WALL_H, WALL_DARK)); // B/mid wall (south half)

  // Short walls near spawns to break sightlines
  boxes.push(box(0, 18, 10, T, WALL_H, WALL_DARK)); // CT spawn front
  boxes.push(box(0, -18, 10, T, WALL_H, WALL_DARK)); // T spawn front

  // ─── Bomb-site A (east) — cluster of crates ───
  boxes.push(box(26, 8, 3, 3, 3, CRATE));
  boxes.push(box(30, 8, 3, 3, 3, CRATE_LIGHT, 3)); // stacked
  boxes.push(box(26, 8, 3, 3, 3, CRATE, 3)); // stacked
  boxes.push(box(31, 2, 3, 3, 2, CRATE));
  boxes.push(box(24, -2, 4, 2, 2.5, CRATE_LIGHT));
  boxes.push(box(29, -6, 3, 3, 3, CRATE));
  boxes.push(box(22, 12, 2.5, 2.5, 2, CRATE_LIGHT));

  // ─── Bomb-site B (west) — mirrored cluster ───
  boxes.push(box(-26, 8, 3, 3, 3, CRATE));
  boxes.push(box(-30, 8, 3, 3, 3, CRATE_LIGHT, 3));
  boxes.push(box(-26, 8, 3, 3, 3, CRATE, 3));
  boxes.push(box(-31, 2, 3, 3, 2, CRATE));
  boxes.push(box(-24, -2, 4, 2, 2.5, CRATE_LIGHT));
  boxes.push(box(-29, -6, 3, 3, 3, CRATE));
  boxes.push(box(-22, 12, 2.5, 2.5, 2, CRATE_LIGHT));

  // ─── Mid cover ───
  boxes.push(box(0, 4, 3, 3, 2.5, CRATE));
  boxes.push(box(0, -6, 2.5, 2.5, 2, CRATE_LIGHT));
  boxes.push(box(4, -1, 2, 2, 1.5, CRATE));
  boxes.push(box(-4, -1, 2, 2, 1.5, CRATE));

  // ─── Corridor cover between spawn and sites ───
  boxes.push(box(18, -20, 3, 3, 2.5, CRATE));
  boxes.push(box(-18, -20, 3, 3, 2.5, CRATE));
  boxes.push(box(18, 20, 3, 3, 2.5, CRATE));
  boxes.push(box(-18, 20, 3, 3, 2.5, CRATE));

  // ─── Spawns ───
  const spawns: Record<Team, SpawnPoint[]> = {
    // T spawns south, looking north (yaw 0 = -Z forward in our convention)
    [Team.T]: [
      { x: -6, z: -26, yaw: 0 },
      { x: -3, z: -27, yaw: 0 },
      { x: 0, z: -26, yaw: 0 },
      { x: 3, z: -27, yaw: 0 },
      { x: 6, z: -26, yaw: 0 },
    ],
    // CT spawns north, looking south (yaw π = +Z forward)
    [Team.CT]: [
      { x: -6, z: 26, yaw: Math.PI },
      { x: -3, z: 27, yaw: Math.PI },
      { x: 0, z: 26, yaw: Math.PI },
      { x: 3, z: 27, yaw: Math.PI },
      { x: 6, z: 26, yaw: Math.PI },
    ],
  };

  return {
    ground: { hw: HALF_X, hd: HALF_Z, color: SAND },
    boxes,
    spawns,
    sites: [
      { name: "A", x: 27, z: 4 },
      { name: "B", x: -27, z: 4 },
    ],
    bounds: { minX: -HALF_X, maxX: HALF_X, minZ: -HALF_Z, maxZ: HALF_Z },
  };
}

export const DUST2_MAP: FpsMap = buildMap();
